/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    LinearProgress,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import StatCard from "../LayoutComponents/StatCard";

import { FiChevronDown } from "react-icons/fi";

const client = new FastAPIClient(config);

type UserCoverageLog = {
    sales_rep_name: string;
    team_name: string;
    group_name: string;
    month: string;
    year: string;
    actual_meetings: number;
    expected_meetings: number;
    coverage: number;
};

interface ClientCoverageTableProps {
    selectedUser: string;
    selectedTeam: string;
    selectedWarehouse: string;
    timePeriod: string;
    startDate;
    endDate;
}

const BarChartInTable: React.FC<{
    value: number;
    maxValue: number;
    totalValue: number;
}> = ({ value, maxValue, totalValue }) => {
    const barWidth = (value / maxValue) * 100;
    const percentage = ((value / totalValue) * 100).toFixed(0);

    return (
        <Box sx={{ position: "relative", width: "100%", height: "10px" }}>
            {/* Bar */}
            <Box
                sx={{
                    position: "absolute",
                    top: 4,
                    left: 0,
                    width: `${barWidth}%`,
                    height: "100%",
                    bgcolor: "#d3d3d3",
                    borderRadius: "5px",
                }}
            />

            {/* Percentage Label */}
            <Typography
                variant="caption"
                sx={{
                    position: "absolute",
                    top: "100%",
                    left: `50%`,
                    transform: "translate(-50%, -50%)",
                    fontSize: "0.65rem",
                    whiteSpace: "nowrap",
                    zIndex: 1,
                }}
            >
                {percentage}%
            </Typography>
        </Box>
    );
};

const ClientCoverageTable: React.FC<ClientCoverageTableProps> = ({
    selectedUser,
    selectedTeam,
    selectedWarehouse,
    timePeriod,
    startDate,
    endDate,
}) => {
    const [data, setData] = useState<UserCoverageLog[] | null>(null);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("week"),
        dayjs().endOf("week"),
    ]);

    useEffect(() => {
        fetchCoverageData();
    }, [selectedWarehouse, selectedUser]);

    const fetchCoverageData = async () => {
        try {
            const today = dayjs().format("YYYY-MM-DD");
            const response = await client.fetchCoverage(
                "2025-01-01",
                today,
                selectedWarehouse,
                selectedUser
            );
            setData(response.results);
            console.log("coverage", response.results);
        } catch (error) {
            console.error("Error fetching coverage data:", error);
        }
    };

    const filteredData = useMemo(() => {
        if (!data || !dateRange[0] || !dateRange[1]) return [];

        // console.log("data", data);

        // const [start, end] = dateRange;
        // let filtered = data;

        // if (selectedTeam !== "all") {
        //     filtered = filtered.filter((d) => d.team_name === selectedTeam);
        // }

        // if (selectedUser !== "all") {
        //     filtered = filtered.filter(
        //         (d) => d.sales_rep_name === selectedUser
        //     );
        // }

        // if (selectedWarehouse !== "all") {
        //     filtered = filtered.filter(
        //         (d) => d.group_name === selectedWarehouse
        //     );
        // }

        return data;
    }, [data, selectedUser, selectedTeam, selectedWarehouse, dateRange]);

    const rows = useMemo(() => {
        if (!filteredData) return [];

        const warehouseMap = new Map<
            string,
            { actual: number; expected: number }
        >();

        filteredData.forEach((entry) => {
            const warehouse = entry.group_name;
            const existing = warehouseMap.get(warehouse) || {
                actual: 0,
                expected: 0,
            };

            existing.actual += entry.actual_meetings;
            existing.expected += entry.expected_meetings;

            warehouseMap.set(warehouse, existing);
        });

        return Array.from(warehouseMap.entries()).map(
            ([warehouse, stats], id) => {
                const coverage =
                    stats.expected > 0
                        ? (stats.actual / stats.expected) * 100
                        : 0;
                return {
                    id,
                    warehouse,
                    coverage: Math.round(coverage),
                };
            }
        );
    }, [filteredData]);

    useEffect(() => {
        console.log(rows);
        console.log("filteredData", filteredData);
    }, [rows]);

    const columns: GridColDef[] = [
        {
            field: "group_name",
            headerName: "Warehouse",
            flex: 1.9,
            renderCell: (params) => {
                console.log(params);
                return params.row.warehouse; // or JSX like <span>{params.value}</span>
            },
        },
        {
            field: "coverage",
            headerName: "Coverage",
            flex: 2,
            renderCell: (params) => (
                <Box width="100%">
                    <BarChartInTable
                        value={params.value}
                        maxValue={100}
                        totalValue={100}
                    />
                </Box>
            ),
        },
    ];

    const [expanded, setExpanded] = useState<boolean>(true);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Accordion
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
                disableGutters
                elevation={0}
                square
                sx={{
                    mb: 1,
                    boxShadow: "none",
                    border: "none",
                    "&::before": {
                        display: "none", // removes the default divider line
                    },
                }}
            >
                <AccordionSummary
                    expandIcon={<FiChevronDown />}
                    sx={{
                        p: 0, // removes all padding
                        m: 0, // removes all margin
                        minHeight: 0,
                        "& .MuiAccordionSummary-content": {
                            m: 0,
                            p: 0,
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                            p: 0, // optional: tighten up the expand icon padding
                            m: 0,
                        },
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        fontSize="0.875rem"
                        sx={{ fontWeight: "bold" }}
                    >
                        Couverture clients
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box>
                        <DataGridPro
                            rows={rows}
                            columns={columns}
                            rowHeight={30}
                            hideFooter
                            disableColumnMenu
                            disableRowSelectionOnClick
                            density="compact"
                            slots={{
                                columnHeaders: () => null,
                            }}
                            sx={{
                                border: "none",
                                overflow: "hidden",
                                "& .MuiDataGrid-root": {
                                    border: "none",
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    borderBottom: "none",
                                },
                                "& .MuiDataGrid-cell": {
                                    fontSize: "0.75rem",
                                    padding: "1px",
                                    borderBottom: "none",
                                    borderTop: "none",
                                    textAlign: "left",
                                    display: "flex",
                                },
                                "& .positive-change": {
                                    color: "#009A0A",
                                },
                                "& .negative-change": {
                                    color: "#FF365E",
                                },
                                // Hover to show scrollbar
                                "&:hover": {
                                    "&::-webkit-scrollbar": {
                                        opacity: 1, // Make scrollbar visible on hover
                                    },
                                },
                                "&::-webkit-scrollbar": {
                                    width: "8px", // Width of the scrollbar
                                    opacity: 0, // Initially hide the scrollbar
                                    transition: "opacity 0.3s", // Smooth transition
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "#f1f1f1", // Light track color
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    background: "#888", // Scrollbar thumb color
                                    // borderRadius: "4px", // Rounded corners for the scrollbar thumb
                                },
                                "&::-webkit-scrollbar-thumb:hover": {
                                    background: "#555", // Hover effect for the scrollbar thumb
                                },
                            }}
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>
        </LocalizationProvider>
    );
};

export default ClientCoverageTable;
